import { Link } from "@/i18n/navigation";
import { type Locale } from "@/i18n/config";
import { ActionToType, ActionType } from "@/lib/types";
import {
  getLocalizedSlug,
  type LocalizedSlug,
} from "@/lib/routing/url-builder";
import { getI18nString } from "@/lib/utils";
import { useLocale } from "next-intl";
import { Button, ButtonProps } from "@repo/ui/button";

/**
 * Resolve a slug that may be a simple string or a localized slug object.
 */
function resolveSlug(
  slug: string | LocalizedSlug | null | undefined,
  locale: Locale,
): string | null {
  if (!slug) return null;
  if (typeof slug === "string") return slug;
  return getLocalizedSlug(slug, locale);
}

interface ActionLinkProps {
  // Schema declares `to` as an array with max(1). GROQ projections normally
  // unwrap it via `to[0]{…}`, but portable-text markDefs pass it through raw,
  // so accept either shape and normalise below.
  actionTo?: ActionToType | ActionToType[];
  slug?: LocalizedSlug | string | null;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  "data-testid"?: string;
  "aria-labelledby"?: string;
  /** Fires on activation, before navigation — used for click analytics. */
  onClick?: () => void;
}

export const ActionLink = ({
  actionTo: rawActionTo,
  slug,
  className,
  style,
  children,
  "data-testid": dataTestId,
  "aria-labelledby": ariaLabelledBy,
  onClick,
}: ActionLinkProps): JSX.Element => {
  const locale = useLocale() as Locale;
  const actionTo: ActionToType | undefined = Array.isArray(rawActionTo)
    ? rawActionTo[0]
    : rawActionTo;

  // Slug-based link — resolves a LocalizedSlug to a locale-aware internal link
  if (slug) {
    const resolved = resolveSlug(slug, locale);
    if (!resolved) {
      return (
        <span
          className={className}
          style={style}
          data-testid={dataTestId}
          aria-labelledby={ariaLabelledBy}
          onClick={onClick}
        >
          {children}
        </span>
      );
    }
    const href = resolved.startsWith("/") ? resolved : `/${resolved}`;
    return (
      <Link
        href={href}
        className={className}
        style={style}
        data-testid={dataTestId}
        aria-labelledby={ariaLabelledBy}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }

  if (!actionTo) {
    return (
      <span
        className={className}
        style={style}
        data-testid={dataTestId}
        aria-labelledby={ariaLabelledBy}
        onClick={onClick}
      >
        {children}
      </span>
    );
  }

  if (actionTo._type === "internal") {
    const resolvedSlug = resolveSlug(
      actionTo.link?.slug as string | LocalizedSlug | null,
      locale,
    );
    let href = "";
    if (resolvedSlug) {
      href = resolvedSlug.startsWith("/") ? resolvedSlug : `/${resolvedSlug}`;
    }
    if (actionTo.link?.anchor) {
      href += `#${actionTo.link.anchor}`;
    }
    if (actionTo.link?.params) {
      const queryParams = new URLSearchParams();
      actionTo.link.params.forEach((p) => {
        if (p.key && p.value) {
          queryParams.append(p.key, p.value);
        }
      });
      if (queryParams.toString()) {
        href += `?${queryParams.toString()}`;
      }
    }

    if (!href && actionTo.link?.anchor) href = `#${actionTo.link.anchor}`;

    if (!href) {
      return (
        <span
          className={className}
          style={style}
          data-testid={dataTestId}
          aria-labelledby={ariaLabelledBy}
          onClick={onClick}
        >
          {children}
        </span>
      );
    }

    const openInNewWindow = actionTo.link?.newWindow;

    return (
      <Link
        href={href}
        target={openInNewWindow ? "_blank" : undefined}
        rel={openInNewWindow ? "noopener noreferrer" : undefined}
        className={className}
        style={style}
        data-testid={dataTestId}
        aria-labelledby={ariaLabelledBy}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }

  if (actionTo._type === "external") {
    if (actionTo.link?.url) {
      const url = actionTo.link.url;
      const isSpecialScheme =
        url.startsWith("mailto:") || url.startsWith("tel:");
      const openInNewWindow = actionTo.link.newWindow && !isSpecialScheme;
      return (
        <a
          href={url}
          target={openInNewWindow ? "_blank" : undefined}
          rel={openInNewWindow ? "noopener noreferrer" : undefined}
          className={className}
          style={style}
          data-testid={dataTestId}
          aria-labelledby={ariaLabelledBy}
          onClick={onClick}
        >
          {children}
        </a>
      );
    }
    return (
      <span
        className={className}
        style={style}
        data-testid={dataTestId}
        aria-labelledby={ariaLabelledBy}
        onClick={onClick}
      >
        {children}
      </span>
    );
  }

  if (actionTo._type === "media") {
    if (actionTo.link?.file?.asset?.url) {
      return (
        <a
          href={actionTo.link.file.asset.url}
          download
          className={className}
          style={style}
          data-testid={dataTestId}
          aria-labelledby={ariaLabelledBy}
          onClick={onClick}
        >
          {children}
        </a>
      );
    }
    return (
      <span
        className={className}
        style={style}
        data-testid={dataTestId}
        aria-labelledby={ariaLabelledBy}
        onClick={onClick}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={style}
      data-testid={dataTestId}
      aria-labelledby={ariaLabelledBy}
      onClick={onClick}
    >
      {children}
    </span>
  );
};

type ResponsiveButtonSize =
  | ButtonProps["size"]
  | { mobile: ButtonProps["size"]; desktop: ButtonProps["size"] };

export interface ActionsProps {
  actions?: ActionType[] | null;
  className?: string;
  actionClassName?: string;
  buttonVariants?: ButtonProps["variant"][];
  buttonSize?: ResponsiveButtonSize;
}

const isResponsiveSize = (
  size: ResponsiveButtonSize | undefined,
): size is { mobile: ButtonProps["size"]; desktop: ButtonProps["size"] } =>
  typeof size === "object" && size !== null;

export const Actions = ({
  actions,
  className,
  actionClassName,
  buttonVariants = [
    "blue-filled",
    "blue-outline",
    "blue-filled",
    "blue-outline",
  ],
  buttonSize,
}: ActionsProps): JSX.Element | null => {
  if (!actions || actions.length === 0) {
    return null;
  }

  const renderList = (
    size: ButtonProps["size"] | undefined,
    extraClassName?: string,
  ) => (
    <div
      className={`flex flex-wrap gap-4 ${className || ""} ${extraClassName || ""}`.trim()}
    >
      {actions.map((action, index) =>
        action && action.to ? (
          <ActionLink
            key={action._id}
            actionTo={action.to}
            className={actionClassName}
          >
            <Button
              variant={buttonVariants[index] || "blue-filled"}
              size={size}
              asChild
            >
              <span>{getI18nString(action.text, "Learn More")}</span>
            </Button>
          </ActionLink>
        ) : null,
      )}
    </div>
  );

  if (isResponsiveSize(buttonSize)) {
    return (
      <>
        {renderList(buttonSize.mobile, "desktop:hidden")}
        {renderList(buttonSize.desktop, "hidden desktop:flex")}
      </>
    );
  }

  return renderList(buttonSize);
};
